import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DetailSection } from '../ui/DetailSection.js';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Textarea } from '../ui/Textarea.js';
import { IconButton } from '../ui/IconButton.js';
import { LoadingState } from '../ui/LoadingState.js';
import { useConfirm } from '../ui/ConfirmDialog.js';
import { useToast } from '../ui/Toast.js';
import { Pill } from '../PillGroup.js';
import { Icon } from '../Icon.js';
import {
  useGenerateContentPlan,
  useUpdateContentPlan,
  useRegeneratePlanPage,
  useStartDesign,
  useGenerateContentReport,
  type ProjectRow,
  type ContentPlan,
  type ContentBlock,
} from '../../hooks/useProjects.js';
import { KVGrid } from '../ui/KVGrid.js';
import { KVRow } from '../ui/KVRow.js';
import { SectionHeaderAction } from '../ui/SectionHeaderAction.js';
import { ContentPlanMarkdownModal } from './ContentPlanMarkdownModal.js';
import { useComposeContentPlanEmail, useProjectDrafts, useProjectReplies } from '../../hooks/useProjectOutreach.js';

// ── Inline SVG icons ──────────────────────────────────────────────────────────

function ChevronUp() {
  return (
    <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="18 15 12 9 6 15" />
    </svg>
  );
}

function ChevronDown() {
  return (
    <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ImageIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  );
}

// ── InlineText — click-to-edit in blueprint context ────────────────────────────

interface InlineTextProps {
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  placeholder?: string;
  multiline?: boolean;
}

function InlineText({ value, onChange, onSave, placeholder = '—', multiline = false }: InlineTextProps) {
  const { t } = useTranslation();
  const [active, setActive] = useState(false);

  const baseStyle: React.CSSProperties = {
    width: '100%',
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid var(--evo-cyan)',
    outline: 'none',
    fontFamily: 'var(--evo-font)',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    color: 'inherit',
    letterSpacing: 'inherit',
    lineHeight: 'inherit',
    padding: 0,
    display: 'block',
  };

  if (active && multiline) {
    return (
      <Textarea
        variant="naked"
        autoFocus
        value={value}
        rows={3}
        onChange={onChange}
        onBlur={() => { setActive(false); onSave(); }}
        resize="vertical"
        fullWidth
      />
    );
  }

  if (active) {
    return (
      <Input
        variant="naked"
        autoFocus
        value={value}
        onChange={onChange}
        onBlur={() => { setActive(false); onSave(); }}
        fullWidth
      />
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={() => setActive(true)}
      onKeyDown={e => { if (e.key === 'Enter') setActive(true); }}
      title={t('projects.contentPlan.placeholder.clickToEdit')}
      style={{
        display: 'block',
        cursor: 'text',
        minHeight: '1.2em',
        color: value ? 'inherit' : 'var(--evo-fg-4)',
        fontFamily: 'var(--evo-font)',
      }}
    >
      {value || placeholder}
    </span>
  );
}

// ── ImagePlaceholder ──────────────────────────────────────────────────────────

function ImagePlaceholder({ height = 140 }: { height?: number }) {
  const { t } = useTranslation();
  return (
    <div style={{
      width: '100%',
      height,
      background: 'var(--evo-bg-2)',
      border: '1px dashed var(--evo-stroke)',
      borderRadius: 'var(--evo-r-sm)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 'var(--evo-space-1)',
      color: 'var(--evo-fg-4)',
      fontFamily: 'var(--evo-font)',
      fontSize: 'var(--evo-fs-label)',
      flexShrink: 0,
    }}>
      <ImageIcon />
      {t('projects.contentPlan.placeholder.image')}
    </div>
  );
}

// ── Block controls header bar ─────────────────────────────────────────────────

interface BlockControlsProps {
  block: ContentBlock;
  isFirst: boolean;
  isLast: boolean;
  open: boolean;
  ready: boolean;
  onToggle: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function BlockControls({ block, isFirst, isLast, open, ready, onToggle, onMoveUp, onMoveDown, onDuplicate, onDelete }: BlockControlsProps) {
  const { t } = useTranslation();
  return (
    <div
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--evo-space-2)',
        padding: 'var(--evo-block-head-py) var(--evo-subrow-px)',
        background: 'var(--evo-log-row-bg)',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <span style={{ display: 'inline-flex', color: 'var(--evo-fg-4)', flexShrink: 0 }}>
        <Icon name="tasks" size={12} />
      </span>
      <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-tiny)', fontFamily: 'var(--evo-font)', textTransform: 'uppercase', letterSpacing: 'var(--evo-ls-caps)', color: 'var(--evo-fg-4)', whiteSpace: 'nowrap' }}>
        {t(`projects.contentPlan.blockTypes.${block.type}`, { defaultValue: block.type })}
      </span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-list-title-fs)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {block.title || t('projects.contentPlan.newSectionTitle')}
      </span>
      {ready ? (
        <Pill variant="outline" tone="cyan" size="xs">{t('projects.contentPlan.block.ready')}</Pill>
      ) : (
        <Pill variant="outline" tone="amber" size="xs">{t('projects.contentPlan.block.missingContent')}</Pill>
      )}
      <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
        <IconButton icon={<ChevronUp />} ariaLabel={t('projects.contentPlan.block.moveUp')} tooltip={t('projects.contentPlan.block.moveUp')} variant="ghost" tone="muted" size="sm" disabled={isFirst} onClick={onMoveUp} />
        <IconButton icon={<ChevronDown />} ariaLabel={t('projects.contentPlan.block.moveDown')} tooltip={t('projects.contentPlan.block.moveDown')} variant="ghost" tone="muted" size="sm" disabled={isLast} onClick={onMoveDown} />
        <IconButton icon={<CopyIcon />} ariaLabel={t('projects.contentPlan.block.duplicate')} tooltip={t('projects.contentPlan.block.duplicate')} variant="ghost" tone="muted" size="sm" onClick={onDuplicate} />
        <IconButton icon={<XIcon />} ariaLabel={t('projects.contentPlan.block.delete')} tooltip={t('projects.contentPlan.block.delete')} variant="ghost" tone="danger" size="sm" onClick={onDelete} />
      </div>
    </div>
  );
}

// ── Editor de sección — texto infográfico ────────────────────────────────────
// El plan es TEXTUAL: por sección un título, prosa redactada desde el brief
// (para validar) y cuadros de recomendación. La estructura/layout sale del
// template o de las referencias del brief, en la pestaña Diseño — acá no se
// maqueta nada. Los cuadros viven en block.notes como líneas con prefijo
// neutro `[visual]` / `[animation]` / `[note]` (REGLA #6): el reporte, la
// página pública y el generador de diseño leen el mismo string sin migración.

type PatchFn = (updates: Partial<ContentBlock>) => void;

type CalloutKind = 'visual' | 'animation' | 'note';
interface PlanCallout { kind: CalloutKind; text: string }

const CALLOUT_RE = /^\[(visual|animation|animacion|animación|note|nota)\]\s*/i;

function parseCallouts(notes: string | undefined): PlanCallout[] {
  if (!notes || notes.trim() === '') return [];
  return notes.split('\n').map(l => l.trim()).filter(Boolean).map(line => {
    const m = CALLOUT_RE.exec(line);
    if (!m) return { kind: 'note' as CalloutKind, text: line };
    const raw = m[1]!.toLowerCase();
    const kind: CalloutKind = raw.startsWith('visual') ? 'visual' : raw.startsWith('anima') ? 'animation' : 'note';
    return { kind, text: line.replace(CALLOUT_RE, '') };
  });
}

function serializeCallouts(callouts: PlanCallout[]): string {
  return callouts.map(c => `[${c.kind}] ${c.text}`).join('\n');
}

const CALLOUT_ACCENT: Record<CalloutKind, string> = {
  visual: 'var(--evo-cyan)',
  animation: 'var(--evo-amber)',
  note: 'var(--evo-fg-3)',
};

function CalloutBox({ callout, onChange, onRemove, onBlurSave }: {
  callout: PlanCallout;
  onChange: (text: string) => void;
  onRemove: () => void;
  onBlurSave: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={{
      border: '1px solid var(--evo-stroke)',
      borderLeft: `2px solid ${CALLOUT_ACCENT[callout.kind]}`,
      borderRadius: 'var(--evo-r)',
      padding: 'var(--evo-subrow-py) var(--evo-subrow-px)',
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--evo-space-1)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
        <span style={{ flex: 1, fontSize: 'var(--evo-fs-tiny)', fontFamily: 'var(--evo-font)', textTransform: 'uppercase', letterSpacing: 'var(--evo-ls-caps)', color: CALLOUT_ACCENT[callout.kind] }}>
          {t(`projects.contentPlan.editor.callout.${callout.kind}`)}
        </span>
        <IconButton icon={<XIcon />} ariaLabel={t('common.delete', { defaultValue: 'Eliminar' })} variant="ghost" tone="muted" size="sm" onClick={onRemove} />
      </div>
      <div onBlur={onBlurSave}>
        <Textarea value={callout.text} onChange={onChange} rows={2} fullWidth resize="none" />
      </div>
    </div>
  );
}

function SectionEditor({ block, patch, save }: { block: ContentBlock; patch: PatchFn; save: () => void }) {
  const { t } = useTranslation();
  const callouts = parseCallouts(block.notes);

  function setCallouts(next: PlanCallout[]) {
    patch({ notes: serializeCallouts(next) });
  }

  function addCallout(kind: CalloutKind) {
    setCallouts([...callouts, { kind, text: '' }]);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
      <div onBlur={save}>
        <Input
          value={block.title}
          onChange={v => patch({ title: v })}
          placeholder={t('projects.contentPlan.editor.sectionTitlePlaceholder')}
          size="sm"
          fullWidth
        />
      </div>
      <div onBlur={save}>
        <Textarea
          value={block.content}
          onChange={v => patch({ content: v })}
          placeholder={t('projects.contentPlan.editor.prosePlaceholder')}
          rows={5}
          fullWidth
          resize="vertical"
        />
      </div>

      {(block.type === 'hero' || block.ctaText !== '') && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
          <div onBlur={save}>
            <Input value={block.ctaText} onChange={v => patch({ ctaText: v })} size="sm" />
          </div>
          <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
            {t('projects.contentPlan.editor.ctaLabel')}
          </span>
        </div>
      )}

      {/* Elementos del plan estructural anterior: se muestran, no se editan. */}
      {block.items.length > 0 && (
        <div>
          <span style={{ fontSize: 'var(--evo-fs-tiny)', fontFamily: 'var(--evo-font)', textTransform: 'uppercase', letterSpacing: 'var(--evo-ls-caps)', color: 'var(--evo-fg-4)' }}>
            {t('projects.contentPlan.editor.legacyItems')}
          </span>
          <ul style={{ margin: 'var(--evo-space-1) 0 0', paddingLeft: 'var(--evo-space-4)', fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', lineHeight: 1.6 }}>
            {block.items.map((it, i) => (
              <li key={i}><strong>{it.label}</strong>{it.description ? ` — ${it.description}` : ''}</li>
            ))}
          </ul>
        </div>
      )}

      {callouts.map((c, i) => (
        <CalloutBox
          key={i}
          callout={c}
          onChange={text => setCallouts(callouts.map((x, j) => j === i ? { ...x, text } : x))}
          onRemove={() => { setCallouts(callouts.filter((_, j) => j !== i)); save(); }}
          onBlurSave={save}
        />
      ))}

      <div style={{ display: 'flex', gap: 'var(--evo-space-2)', flexWrap: 'wrap' }}>
        <Button variant="ghost" tone="cyan" size="sm" onClick={() => addCallout('visual')}>{t('projects.contentPlan.editor.addVisual')}</Button>
        <Button variant="ghost" tone="cyan" size="sm" onClick={() => addCallout('animation')}>{t('projects.contentPlan.editor.addAnimation')}</Button>
        <Button variant="ghost" tone="muted" size="sm" onClick={() => addCallout('note')}>{t('projects.contentPlan.editor.addNote')}</Button>
      </div>
    </div>
  );
}

function renderBlockBody(block: ContentBlock, patch: PatchFn, save: () => void) {
  return <SectionEditor block={block} patch={patch} save={save} />;
}

// ── ContentPlanSection ────────────────────────────────────────────────────────

export interface ContentPlanSectionProps {
  project: ProjectRow;
  plan: ContentPlan | null;
  onPlanChange: (p: ContentPlan | null) => void;
  divider?: boolean;
  onDesignStarted?: () => void;
  onReportGenerated?: () => void;
  onSendContentPlan?: () => void;
}

export function ContentPlanSection({ project, plan, onPlanChange, divider = true, onDesignStarted, onReportGenerated, onSendContentPlan }: ContentPlanSectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const [markdownOpen, setMarkdownOpen] = useState(false);
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();

  const generateMut = useGenerateContentPlan();
  const regenPageMut = useRegeneratePlanPage();
  const updateMut = useUpdateContentPlan();
  const startDesignMut = useStartDesign();
  const generateReportMut = useGenerateContentReport();
  const sendContentPlanMut = useComposeContentPlanEmail(project.id);
  const { data: draftsData } = useProjectDrafts(project.id);
  const { data: repliesData } = useProjectReplies(project.id);

  const cpReviewDraftIds = new Set(
    (draftsData?.drafts ?? [])
      .filter(d => d.draftType === 'content_plan_review' && d.status === 'sent')
      .map(d => d.id),
  );
  const contentPlanReply = [...(repliesData?.replies ?? [])]
    .sort((a, b) => b.receivedAt - a.receivedAt)
    .find(r => r.matchedDraftId !== null && cpReviewDraftIds.has(r.matchedDraftId)) ?? null;

  const planRef = useRef<ContentPlan | null>(plan);
  useEffect(() => { planRef.current = plan; }, [plan]);

  // ── Páginas del plan ───────────────────────────────────────────────────────
  // Planes viejos no traen `pages`: una sola página. Bloques sin `page`
  // cuelgan de la primera.
  const pageNames = plan?.pages && plan.pages.length > 0 ? plan.pages : ['Inicio'];
  const firstPage = pageNames[0]!;
  const [activePage, setActivePage] = useState(firstPage);
  useEffect(() => {
    if (!pageNames.includes(activePage)) setActivePage(firstPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resincronizar solo cuando cambia la lista de páginas
  }, [pageNames.join('|')]);

  useEffect(() => {
    const first = (plan?.blocks ?? [])
      .filter(b => blockPageOf(b) === activePage)
      .sort((a, b) => a.order - b.order)[0]?.id;
    setOpenBlocks(first ? new Set([first]) : new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- abrir la primera sección solo al cambiar de página
  }, [activePage]);

  const blockPageOf = (b: ContentBlock) => b.page ?? firstPage;

  // Fila colapsada por defecto; al entrar a una página se abre su primera
  // sección (así el hero queda a la vista, como en el diseño).
  const [openBlocks, setOpenBlocks] = useState<Set<string>>(new Set());
  const blockFilled = (b: ContentBlock) =>
    b.content.trim() !== '' || b.items.some(it => it.label.trim() !== '' || it.description.trim() !== '');
  function toggleBlock(id: string) {
    setOpenBlocks(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  const pageBlocks = (plan?.blocks ?? [])
    .filter(b => blockPageOf(b) === activePage)
    .sort((a, b) => a.order - b.order);

  function setPlan(p: ContentPlan | null) {
    planRef.current = p;
    onPlanChange(p);
  }

  async function autoSave(p: ContentPlan) {
    try {
      await updateMut.mutateAsync({ id: project.id, contentPlan: p });
    } catch { /* non-blocking */ }
  }

  async function handleGenerate() {
    try {
      const result = await generateMut.mutateAsync(project.id);
      setPlan(result.contentPlan);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  }

  // Regenerar actúa SOLO sobre la página activa; las demás no se tocan.
  async function handleRegeneratePage() {
    const ok = await confirmAction({
      title: t('projects.contentPlan.regeneratePage'),
      description: t('projects.contentPlan.regeneratePageConfirm', { page: activePage }),
      confirmLabel: t('projects.contentPlan.regeneratePage'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const result = await regenPageMut.mutateAsync({ id: project.id, page: activePage });
      setPlan(result.contentPlan);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  }

  async function handleGenerateReport() {
    try {
      const result = await generateReportMut.mutateAsync(project.id);
      setPlan(result.contentPlan);
      toast.success(t('projects.contentPlan.reportGenerated'), {
        action: {
          label: t('projects.contentPlan.viewReport'),
          onClick: () => navigate(`/reports/${result.reportId}`),
        },
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  }

  async function handleStartDesign() {
    try {
      await startDesignMut.mutateAsync(project.id);
      onDesignStarted?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  }

  // Las operaciones van por id y viven dentro de la página activa: el orden
  // se renumera por página, los bloques de otras páginas no se tocan.

  function rebuildWithPage(pageOrdered: ContentBlock[]): ContentPlan | null {
    const p = planRef.current;
    if (!p) return null;
    const others = p.blocks.filter(b => blockPageOf(b) !== activePage);
    const renumbered = pageOrdered.map((b, i) => ({ ...b, order: i, page: activePage }));
    return { ...p, blocks: [...others, ...renumbered], lastEditedAt: new Date().toISOString() };
  }

  function updateBlock(id: string, updated: ContentBlock) {
    if (!planRef.current) return;
    const blocks = planRef.current.blocks.map(b => b.id === id ? updated : b);
    setPlan({ ...planRef.current, blocks, lastEditedAt: new Date().toISOString() });
  }

  function saveBlock() {
    if (planRef.current) void autoSave(planRef.current);
  }

  function currentPageBlocks(): ContentBlock[] {
    return (planRef.current?.blocks ?? [])
      .filter(b => blockPageOf(b) === activePage)
      .sort((a, b) => a.order - b.order);
  }

  function moveBlock(id: string, dir: -1 | 1) {
    const inPage = currentPageBlocks();
    const i = inPage.findIndex(b => b.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= inPage.length) return;
    [inPage[i], inPage[j]] = [inPage[j]!, inPage[i]!];
    const p = rebuildWithPage(inPage);
    if (!p) return;
    setPlan(p);
    void autoSave(p);
  }

  function duplicateBlock(id: string) {
    const inPage = currentPageBlocks();
    const i = inPage.findIndex(b => b.id === id);
    if (i < 0) return;
    const dup: ContentBlock = { ...inPage[i]!, id: `block-${Date.now()}` };
    inPage.splice(i + 1, 0, dup);
    const p = rebuildWithPage(inPage);
    if (!p) return;
    setPlan(p);
    void autoSave(p);
  }

  function addBlock() {
    const inPage = currentPageBlocks();
    const nuevo: ContentBlock = {
      id: `block-${Date.now()}`,
      type: 'custom',
      title: t('projects.contentPlan.newSectionTitle'),
      subtitle: '',
      content: '',
      items: [],
      ctaText: '',
      order: inPage.length,
      page: activePage,
    };
    const p = rebuildWithPage([...inPage, nuevo]);
    if (!p) return;
    setPlan(p);
    void autoSave(p);
  }

  async function deleteBlock(id: string) {
    const ok = await confirmAction({
      title: t('projects.contentPlan.block.delete'),
      description: t('projects.contentPlan.block.deleteConfirm'),
      confirmLabel: t('common.delete', { defaultValue: 'Eliminar' }),
      tone: 'danger',
    });
    if (!ok) return;
    const inPage = currentPageBlocks().filter(b => b.id !== id);
    const p = rebuildWithPage(inPage);
    if (!p) return;
    setPlan(p);
    void autoSave(p);
  }

  const isGenerating   = generateMut.isPending;
  const isSavingDesign = startDesignMut.isPending;
  const isReporting    = generateReportMut.isPending;
  const canStartDesign = project.status !== 'in_design' && !!plan && plan.blocks.length > 0;

  // ── Empty state ───────────────────────────────────────────────────────────

  if (!plan) {
    return (
      <DetailSection title={t('projects.contentPlan.title')} divider={divider}>
        {isGenerating ? (
          <div style={{ padding: 'var(--evo-space-4) 0' }}>
            <LoadingState size="sm" label={t('projects.contentPlan.generating')} />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 'var(--evo-space-3)', padding: 'var(--evo-space-3) 0' }}>
            <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-text-muted)', fontFamily: 'var(--evo-font)' }}>
              {t('projects.contentPlan.empty')}
            </span>
            <Button tone="cyan" size="sm" onClick={() => { void handleGenerate(); }}>
              {t('projects.contentPlan.generate')}
            </Button>
          </div>
        )}
        {confirmDialog}
      </DetailSection>
    );
  }

  // ── Vista por páginas ─────────────────────────────────────────────────────

  const reportAt = plan.reportGeneratedAt ? new Date(plan.reportGeneratedAt).getTime() : 0;
  const editedAt = new Date(plan.lastEditedAt).getTime();
  const reportState: 'none' | 'current' | 'stale' = !plan.reportGeneratedAt
    ? 'none'
    : editedAt <= reportAt
    ? 'current'
    : 'stale';

  const pagesWithBlocks = pageNames.filter(pg => plan.blocks.some(b => blockPageOf(b) === pg));
  const missingPages = pageNames.filter(pg => !plan.blocks.some(b => blockPageOf(b) === pg));
  const missingContent = plan.blocks
    .filter(b => !blockFilled(b))
    .map(b => `${t(`projects.contentPlan.blockTypes.${b.type}`, { defaultValue: b.type })} · ${blockPageOf(b)}`);
  const sent = cpReviewDraftIds.size > 0;

  return (
    <>
      {confirmDialog}
      <ContentPlanMarkdownModal projectId={project.id} open={markdownOpen} onClose={() => setMarkdownOpen(false)} />

      {/* Selector de página — el plan se trabaja página por página */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 'var(--evo-space-2)', marginBottom: 'var(--evo-space-4)', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--evo-fs-caption)', fontWeight: 'var(--evo-fw-medium)', letterSpacing: 'var(--evo-ls-caps)', textTransform: 'uppercase', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
          {t('projects.contentPlan.pageLabel')}
        </span>
        {pageNames.map(pg => (
          <Pill key={pg} tone="cyan" variant="outline" size="sm" active={pg === activePage} onClick={() => setActivePage(pg)}>
            {pg}
          </Pill>
        ))}
      </div>

      {/* Secciones de la página activa */}
      <DetailSection
        title={t('projects.contentPlan.sectionsOf', { page: activePage })}
        divider={false}
        titleRight={
          <>
            <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-tiny)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)', whiteSpace: 'nowrap' }}>
              {t('projects.contentPlan.sectionsCount', { count: pageBlocks.length })}
            </span>
            <SectionHeaderAction onClick={addBlock}>
              {t('projects.contentPlan.addSection')}
            </SectionHeaderAction>
            <SectionHeaderAction tone="muted" onClick={() => { void handleRegeneratePage(); }}>
              {t('projects.contentPlan.regeneratePage')}
            </SectionHeaderAction>
          </>
        }
      >
        {regenPageMut.isPending ? (
          <div style={{ padding: 'var(--evo-space-4) 0' }}>
            <LoadingState size="sm" label={t('projects.contentPlan.generating')} />
          </div>
        ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
          {pageBlocks.map((block, idx) => (
            <div
              key={block.id}
              style={{
                border: '1px solid var(--evo-stroke)',
                borderRadius: 'var(--evo-r)',
                overflow: 'hidden',
              }}
            >
              <BlockControls
                block={block}
                isFirst={idx === 0}
                isLast={idx === pageBlocks.length - 1}
                open={openBlocks.has(block.id)}
                ready={blockFilled(block)}
                onToggle={() => toggleBlock(block.id)}
                onMoveUp={() => moveBlock(block.id, -1)}
                onMoveDown={() => moveBlock(block.id, 1)}
                onDuplicate={() => duplicateBlock(block.id)}
                onDelete={() => { void deleteBlock(block.id); }}
              />
              {openBlocks.has(block.id) && (
                <div style={{ padding: 'var(--evo-block-body-py) var(--evo-subrow-px)', borderTop: '1px solid var(--evo-stroke)' }}>
                  {renderBlockBody(
                    block,
                    (updates) => updateBlock(block.id, { ...block, ...updates }),
                    saveBlock,
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        )}
      </DetailSection>

      {/* Estado del plan | Enviar a revisión */}
      <div className="evo-duo-grid" style={{ marginTop: 'var(--evo-space-4)' }}>
        <DetailSection
          title={t('projects.contentPlan.status.title')}
          divider={false}
          titleRight={
            <SectionHeaderAction tone="muted" onClick={() => setMarkdownOpen(true)}>
              {t('projects.contentPlan.markdown.action')}
            </SectionHeaderAction>
          }
        >
          <KVGrid columns={1}>
            <KVRow
              label={t('projects.contentPlan.status.pagesPlanned')}
              value={t('projects.contentPlan.status.pagesPlannedValue', { done: pagesWithBlocks.length, total: pageNames.length })}
            />
            <KVRow
              label={t('projects.contentPlan.status.sections')}
              value={t('projects.contentPlan.status.sectionsValue', { count: plan.blocks.length })}
            />
            <KVRow
              label={t('projects.contentPlan.status.generatedBy')}
              value={`EvoBrain · ${new Date(plan.generatedAt).toLocaleDateString('es', { day: 'numeric', month: 'short', year: '2-digit' })}`}
            />
            <KVRow
              label={t('projects.contentPlan.status.missing')}
              value={(() => {
                const parts = [
                  ...(missingPages.length > 0 ? [t('projects.contentPlan.status.missingPages', { pages: missingPages.join(', ') })] : []),
                  ...missingContent,
                ];
                return parts.length > 0 ? parts.join(', ') : t('projects.contentPlan.status.nothingMissing');
              })()}
              last
            />
          </KVGrid>
        </DetailSection>

        <DetailSection
          title={t('projects.contentPlan.review.title')}
          divider={false}
          titleRight={
            sent
              ? <Pill variant="outline" tone="cyan" size="xs">{t('projects.contentPlan.review.sentPill')}</Pill>
              : <Pill variant="outline" tone="amber" size="xs">{t('projects.contentPlan.review.notSentPill')}</Pill>
          }
        >
          <p style={{ margin: 0, fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', lineHeight: 1.65 }}>
            {t('projects.contentPlan.review.body')}
          </p>

          {contentPlanReply !== null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', paddingTop: 'var(--evo-space-3)' }}>
              <Pill variant="outline" tone="cyan" size="sm">
                {t('projects.contentPlan.clientReview.replied')}
              </Pill>
              <span style={{ fontSize: 'var(--evo-fs-xs)', opacity: 0.6 }}>
                {new Date(contentPlanReply.receivedAt).toLocaleDateString()}
              </span>
            </div>
          )}

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--evo-space-2)',
            justifyContent: 'flex-end',
            paddingTop: 'var(--evo-space-4)',
            flexWrap: 'wrap',
          }}>
            {reportState === 'current' ? (
              <Button
                variant="ghost"
                tone="muted"
                size="sm"
                onClick={() => {
                  const target = plan.reportId ?? plan.reportSlug;
                  if (target) navigate(`/reports/${target}`);
                  else onReportGenerated?.();
                }}
              >
                {t('projects.contentPlan.reportIsUpToDate', { defaultValue: 'Reporte generado · Ver' })} →
              </Button>
            ) : (
              <Button variant="outline" tone="muted" size="sm" loading={isReporting} onClick={() => { void handleGenerateReport(); }}>
                {t('projects.contentPlan.generateReport')}
              </Button>
            )}
            <Button
              variant="outline"
              tone="cyan"
              size="sm"
              loading={sendContentPlanMut.isPending}
              onClick={() => {
                sendContentPlanMut.mutate(undefined, {
                  onSuccess: () => onSendContentPlan?.(),
                });
              }}
            >
              {sent
                ? t('projects.contentPlan.clientReview.resend')
                : t('projects.contentPlan.clientReview.send')}
            </Button>
            <Button
              variant="solid"
              tone="cyan"
              size="sm"
              disabled={!canStartDesign}
              loading={isSavingDesign}
              onClick={() => { void handleStartDesign(); }}
            >
              {t('projects.contentPlan.clientReview.advanceToDesign')}
            </Button>
          </div>
        </DetailSection>
      </div>
    </>
  );
}